import { Breadcrumbs, Grid, Link, Stack, Typography } from '@mui/material'
import React, { useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import {
  Navigate,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom'

import PageContainer from 'components/common/PageContainer'
import Icon from 'components/Icon'
import { Header } from 'components/navigation/Header'
import DeleteTeamExternalIntegration from 'components/rbac-settings/teams/DeleteTeamExternalIntegration'
import { EditAuthenticationForm } from 'components/status-checks/edit/EditAuthenticationForm'
import { useGetStatusChecksUrls } from 'components/status-checks/hooks/use-get-status-checks-urls'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useTranslation } from 'hooks/use-translation'
import toast from 'lib/toast'
import { muiTheme } from 'mui-theme'
import {
  ObservabilityToolType,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const EditAuthentication: React.FC = () => {
  const statusChecksUrls = useGetStatusChecksUrls()
  const { trans } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { state: locationState } = location
  const { referrer = statusChecksUrls.base() } = locationState ?? {}

  const {
    isCreatingAuthenticationOnly,
    isEdit,
    setIsCreatingAuthenticationOnly,
    setIsEdit
  } = useContext(ObservabilityToolAuthenticationContext)

  // These useEffects seem to be a necessary pattern commonly found in consumers of 'ObservabilityToolAuthenticationContext'
  useEffect(() => {
    if (isCreatingAuthenticationOnly || !setIsCreatingAuthenticationOnly) return

    setIsCreatingAuthenticationOnly(true)
  }, [isCreatingAuthenticationOnly, setIsCreatingAuthenticationOnly])

  useEffect(() => {
    if (isEdit || !setIsEdit) return

    setIsEdit(true)
  }, [isEdit, setIsEdit])

  // These come from the route where 'domain' is an optional segment
  const { domain, observabilityToolType, integrationName } = useParams<{
    domain?: string
    integrationName: string
    observabilityToolType: ObservabilityToolType
  }>()

  // We already presume to re-route users in this manner via the `StatusChecksIndex` component.
  // This adds an extra layer of type safety to satisfy our linter.
  if (!integrationName || !observabilityToolType) {
    return <Navigate to={statusChecksUrls.authentication()} />
  }

  // We transform the params format to conform to the expected row data (type TeamExternalIntegration)
  const integration: Pick<
    TeamExternalIntegration,
    'domain' | 'observabilityToolType' | 'name' | 'type'
  > = {
    domain,
    observabilityToolType,
    name: integrationName ?? '',
    type: observabilityToolType.toLowerCase() as IntegrationType
  }

  const displayIntegrationName =
    observabilityToolType === ObservabilityToolType.AWS && domain
      ? `${integrationName} - ${domain}`
      : integrationName ?? 'Other'

  return (
    <>
      <Helmet title={trans('status_checks.edit_authentication')} />
      <Header>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            data-test-status-check-back-link
            component={RouterLink}
            to={referrer === '/' ? statusChecksUrls.base() : referrer}
            underline="hover"
          >
            <Icon css={{ marginRight: muiTheme.spacing(1) }} icon="wavePulse" />
            Health Checks
          </Link>
          <Link
            data-test-status-check-auth-list-link
            component={RouterLink}
            to={statusChecksUrls.authentication()}
            underline="hover"
          >
            Authentication List
          </Link>
          <Typography color="text.primary">Edit Authentication</Typography>
        </Breadcrumbs>
      </Header>
      <PageContainer>
        <Stack mb={3} spacing={3}>
          <Grid container gap={2}>
            <Grid item>
              <Typography data-test-edit-status-check-title variant="h5">
                {trans(
                  'status_checks.create.edit_authentication.header.title',
                  { integrationName: displayIntegrationName }
                )}
              </Typography>
            </Grid>
            <Grid item>
              <DeleteTeamExternalIntegration
                buttonProps={{ size: 'small' }}
                externalIntegrationType="STATUS_CHECK"
                integration={integration}
                onChanged={() => {
                  toast({
                    type: 'success',
                    text: trans(
                      'status_checks.authentication_list.revoke.success',
                      { name: displayIntegrationName }
                    )
                  })
                  navigate(statusChecksUrls.authentication())
                }}
              />
            </Grid>
          </Grid>
        </Stack>
        <EditAuthenticationForm
          authenticationName={integrationName}
          domain={domain}
          observabilityToolType={observabilityToolType}
        />
      </PageContainer>
    </>
  )
}
